import { renderColumnIdentity } from './column-identity.js';

// #6275: Add/Edit modal for the transaction column
// settings, shared by Ledgers and Events & Transactions. Built on demand so neither page needs extra
// markup; styled by main.css's ctcc-* classes, the same ones the cap table's configuration modal uses.

const MODAL_ID = 'customColumnModal';
const FORMATS = [
	['text', 'Text'],
	['currency', 'Currency'],
	['number', 'Number'],
	['date', 'Date'],
];

export const CUSTOM_COLUMN_FORMAT_LABELS = Object.fromEntries(FORMATS);

function ensureModal() {
	let el = document.getElementById(MODAL_ID);
	if (el) return el;

	el = document.createElement('div');
	el.id = MODAL_ID;
	el.className = 'modal modal-xs ctcc-edit-modal column-edit-modal';
	el.innerHTML = `
		<div class="modal-content ctcc-edit-content">
			<div class="ctcc-edit-header"><h6></h6></div>
			<div class="ctcc-edit-field">
				<label for="customColumnLabel">Label <span id="customColumnReset" class="reset-link" style="display:none;">Reset label</span></label>
				<input type="text" id="customColumnLabel" class="browser-default" placeholder="e.g., Internal Notes" autocomplete="off">
				<div id="customColumnWarning" class="ctcc-label-warning" style="display:none;"></div>
			</div>
			<div class="ctcc-edit-field" id="customColumnIdentityField" style="display:none;">
				<div id="customColumnIdentity" class="ctcc-identity"></div>
			</div>
			<div class="ctcc-edit-field" id="customColumnFormatField">
				<label for="customColumnFormat">Format</label>
				<select id="customColumnFormat" class="browser-default">
					${FORMATS.map(([value, text]) => `<option value="${value}">${text}</option>`).join('')}
				</select>
			</div>
			<div class="ctcc-edit-field" id="customColumnShowField">
				<label><input type="checkbox" id="customColumnShow" /><span>Show in table</span></label>
			</div>
			<div class="ctcc-edit-field" id="customColumnExportField">
				<label><input type="checkbox" id="customColumnExport" /><span>Include in export</span></label>
			</div>
			<div class="ctcc-edit-actions">
				<button type="button" id="customColumnCancel" class="btn-flat waves-effect">Cancel</button>
				<button type="button" id="customColumnApply" class="btn waves-effect waves-light bg-grey-darker"></button>
			</div>
		</div>`;
	document.body.appendChild(el);
	M.Modal.init(el);
	return el;
}

/**
 * Open the modal to add a column or edit one.
 * @param {string} opts.title
 * @param {string} opts.applyText
 * @param {boolean} [opts.showFormat=true] - false hides Format
 * @param {boolean} [opts.lockFormat] - show Format but disable it (built-in columns, whose format is fixed)
 * @param {object|function} [opts.identity] - { type, key, ... } for the "</> key · chip" line (see column-identity.js), or
 *   a function of the current label for a key that follows it; nothing is shown without it
 * @param {string} [opts.defaultLabel] - when set, a "Reset label" link puts it back in the input
 * @param {boolean} [opts.lockExport] - disable "Include in export" (always-exported columns)
 * @param {boolean} [opts.showVisibility] - show a "Show in table" checkbox (a table that also exports hidden columns)
 * @param {boolean} [opts.showExport=true] - false hides "Include in export" (Events & Transactions exports what its tables show)
 * @param {{label?: string, format?: string, includeInExport?: boolean, showInTableUI?: boolean}} opts.initial
 * @param {(label: string) => string|null} opts.validate - message to show (and block Apply), or null
 * @param {(values: {label: string, format: string, includeInExport: boolean, showInTableUI: boolean}) => void} opts.onApply
 */
export function openCustomColumnModal({
	title,
	applyText,
	showExport = true,
	showFormat = true,
	lockFormat = false,
	identity = null,
	defaultLabel = '',
	lockExport = false,
	showVisibility = false,
	initial = {},
	validate,
	onApply,
}) {
	const el = ensureModal();
	const $ = (selector) => el.querySelector(selector);
	const labelInput = $('#customColumnLabel');
	const warning = $('#customColumnWarning');
	const identityField = $('#customColumnIdentityField');
	const formatSelect = $('#customColumnFormat');
	const exportBox = $('#customColumnExport');
	const showBox = $('#customColumnShow');
	const applyBtn = $('#customColumnApply');
	const modal = M.Modal.getInstance(el);

	$('h6').textContent = title;
	applyBtn.textContent = applyText;
	labelInput.value = initial.label || '';
	formatSelect.value = initial.format || 'text';
	exportBox.checked = initial.includeInExport !== false;
	showBox.checked = initial.showInTableUI !== false;
	$('#customColumnShowField').style.display = showVisibility ? '' : 'none';
	$('#customColumnExportField').style.display = showExport ? '' : 'none';
	$('#customColumnFormatField').style.display = showFormat ? '' : 'none';
	exportBox.disabled = lockExport;
	formatSelect.disabled = lockFormat;
	const resetLink = $('#customColumnReset');
	resetLink.style.display = defaultLabel ? '' : 'none';

	// handlers are assigned (not added) so reopening doesn't stack them
	function check() {
		const label = labelInput.value.trim();
		const info = typeof identity === 'function' ? (label ? identity(label) : null) : identity;
		identityField.style.display = info ? '' : 'none';
		if (info) renderColumnIdentity($('#customColumnIdentity'), info);
		const message = label ? validate(label) : null;
		warning.textContent = message || '';
		warning.style.display = message ? '' : 'none';
		const ok = !!label && !message;
		applyBtn.classList.toggle('disabled', !ok);
		return ok;
	}

	labelInput.oninput = check;
	resetLink.onclick = () => {
		labelInput.value = defaultLabel;
		check();
	};
	labelInput.onkeydown = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			applyBtn.click();
		}
	};
	applyBtn.onclick = (e) => {
		e.preventDefault();
		if (!check()) return;
		const values = {
			label: labelInput.value.trim(),
			format: formatSelect.value,
			includeInExport: exportBox.checked,
			showInTableUI: showVisibility ? showBox.checked : true,
		};
		modal.close();
		onApply(values);
	};
	$('#customColumnCancel').onclick = (e) => {
		e.preventDefault();
		modal.close();
	};

	modal.open();
	check();
	labelInput.focus();
}
